import { Component, DestroyRef, ElementRef, HostListener, effect, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CosService, CosChatResponse, CosToolInvocation, NotebookEntryDto } from '../../services/cos.service';
import { AgentTurn } from '../../services/agent.service';
import { ProspectsService } from '../../services/prospects.service';
import { BreakdownService, BreakdownProposal, BreakdownSubtask } from '../../services/breakdown.service';
import { PipelineService } from '../../services/pipeline.service';
import { TimerService, formatElapsed } from '../../services/timer.service';
import { extractErrorMessage } from '../../shared/errors';

interface CosTurn extends AgentTurn {
  toolInvocations?: CosToolInvocation[];
}

/**
 * Phase 3 Pass 2 — phrases that auto-engage ease mode for the user. Conservative
 * by design: false positives drag the user into a mode they didn't ask for, so
 * we anchor on first-person distress phrasings rather than bare keywords like
 * "stuck" (which often appears in mundane contexts like "stuck in traffic").
 * Match is case-insensitive across the whole draft.
 */
const OVERWHELM_PATTERNS: readonly RegExp[] = [
  /\b(i'?m|i am|feeling|feel)\s+(overwhelmed|drowning|spiraling|burned?\s*out|fried)\b/i,
  /\bi'?m\s+stuck\b/i,
  /\b(can'?t\s+(even|think\s+straight|do\s+this(\s+anymore)?)|freaking\s+out|panicking)\b/i,
  /\b(this\s+is\s+too\s+much|too\s+much\s+going\s+on)\b/i,
];

/**
 * The Chief-of-Staff bar: a persistent input fixed to the bottom of the shell
 * on every page. Lets the user dump arbitrary thoughts (substack ideas, jokes,
 * research seeds, family context, reminders, etc.) without navigating to the
 * /capture page. The backend (ClaudeCosService) decides — opinionatedly —
 * whether each dump becomes a Signal, a NotebookEntry, both, or neither, and
 * surfaces what it did so the user can see and override.
 *
 * Conversation history is kept in-memory for the session. Persistence (so
 * yesterday's exchange is still there when you reload tomorrow) is intentionally
 * deferred; the dumps themselves live in Signals + Notebooks where they belong.
 */
@Component({
  selector: 'app-cos-bar',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './cos-bar.component.html',
  styleUrls: ['./cos-bar.component.scss'],
})
export class CosBarComponent {
  private readonly cos = inject(CosService);
  private readonly prospects = inject(ProspectsService);
  private readonly breakdown = inject(BreakdownService);
  private readonly pipeline = inject(PipelineService);
  protected readonly timer = inject(TimerService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Helper exposed to the template for "1:23" / "1:02:08" formatting. */
  protected readonly formatElapsed = formatElapsed;
  protected readonly timerError = signal<string | null>(null);

  /**
   * When the timer is running, the COS composer collapses to a small icon
   * button so the user can still dump a thought mid-focus without losing the
   * timer surface. This signal controls whether the floating composer panel
   * is currently open. Hidden by default whenever the timer is active.
   */
  protected readonly composerExpanded = signal(false);

  protected toggleComposer(): void {
    this.composerExpanded.update(v => !v);
    if (this.composerExpanded()) {
      // Focus the textarea after Angular paints the panel — same trick as
      // the breakdown focus path so keyboards on mobile come up cleanly.
      queueMicrotask(() => this.textareaRef()?.nativeElement.focus());
    }
  }

  protected collapseComposer(): void {
    this.composerExpanded.set(false);
  }

  protected readonly history = signal<CosTurn[]>([]);
  protected readonly draft = signal('');
  protected readonly busy = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly expanded = signal(false);

  /**
   * Phase 3 ADHD design — stuck mode (Barkley EF #3 externalization). When true,
   * the COS bar visually softens, the placeholder eases, and the chat request
   * carries `stuckMode: true` so the backend prepends the StuckModePostscript to
   * Claude's system prompt. Persists across messages AND across reloads (Pass 3
   * localStorage), so overwhelm doesn't reset just because the user navigated.
   */
  protected readonly stuckMode = signal(CosBarComponent.readStoredEase());

  /**
   * Phase 2 Pass 3 — proactive typing-time recall. As the user types, debounced
   * notebook search surfaces "you've written about this before" hints above
   * the composer. Closes the loop on Point of Performance: recall happens at
   * the moment of capture, not behind a search box afterward.
   */
  protected readonly draftHits = signal<NotebookEntryDto[]>([]);
  protected readonly draftHintsExpanded = signal(false);
  private draftSearchTimer?: ReturnType<typeof setTimeout>;

  private static readonly EaseStorageKey = 'cc.cos-bar.ease';

  private static readStoredEase(): boolean {
    try { return globalThis.localStorage?.getItem(CosBarComponent.EaseStorageKey) === '1'; }
    catch { return false; }
  }

  constructor() {
    // Persist ease mode across reloads. The effect runs once on init (writing
    // the current value back, harmless) and again any time the signal changes.
    effect(() => {
      const v = this.stuckMode();
      try { globalThis.localStorage?.setItem(CosBarComponent.EaseStorageKey, v ? '1' : '0'); }
      catch { /* localStorage unavailable — silent no-op, mode still works in-memory */ }
    });

    // Clean up any pending debounced search timer when the bar tears down.
    // Without this, a timer scheduled mid-typing fires into a destroyed
    // component and may resolve a stale search response into a stale signal.
    this.destroyRef.onDestroy(() => {
      if (this.draftSearchTimer) {
        clearTimeout(this.draftSearchTimer);
        this.draftSearchTimer = undefined;
      }
    });

    // Phase 1 SPEC v2 §6.8 Step 3 — the active-timer restore is NOT fired here.
    // It used to be (`this.timer.loadCurrent()` in this constructor) but that
    // raced MSAL on a hard refresh: the bar mounts before the interceptor has a
    // settled token, so the GET /api/timer/current failed and the swallowed
    // catch left the bar un-restored. Restore now fires from AuthService's
    // isAuthenticated effect — the same auth-ready hook that starts the realtime
    // hub — so the GET always carries a token. The bar just reads timer.active().
  }

  private readonly textareaRef = viewChild<ElementRef<HTMLTextAreaElement>>('input');
  private readonly fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly breakdownInputRef = viewChild<ElementRef<HTMLInputElement>>('breakdownInput');

  /**
   * Phase 1 (SPEC v2 §6.8) — breakdown surface state. Lives in the right half
   * of the bar. The proposal is held in a signal until the user confirms or
   * discards; nothing is persisted until confirm. Discard is the no-op safe
   * exit (closes the card, clears the input).
   */
  protected readonly breakdownText = signal('');
  protected readonly breakdownBusy = signal(false);
  protected readonly breakdownConfirming = signal(false);
  protected readonly breakdownError = signal<string | null>(null);
  protected readonly breakdownProposal = signal<BreakdownProposal | null>(null);

  /**
   * Global Cmd+Shift+B (Ctrl+Shift+B on non-Mac) — focuses the breakdown
   * input from any page. Cheap because the listener bails immediately on the
   * other 99.99% of keypresses; an Angular HostListener cleans itself up
   * when the component is destroyed (which is never for this bar, but the
   * contract is correct).
   */
  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(ev: KeyboardEvent): void {
    if (ev.shiftKey && (ev.metaKey || ev.ctrlKey) && (ev.key === 'B' || ev.key === 'b')) {
      ev.preventDefault();
      this.focusBreakdown();
    }
  }

  private focusBreakdown(): void {
    queueMicrotask(() => this.breakdownInputRef()?.nativeElement.focus());
  }

  protected onBreakdownInput(text: string): void {
    this.breakdownText.set(text);
  }

  protected onBreakdownKey(ev: KeyboardEvent): void {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      void this.runBreakdown();
    } else if (ev.key === 'Escape') {
      this.discardBreakdown();
    }
  }

  protected async runBreakdown(): Promise<void> {
    const text = this.breakdownText().trim();
    if (!text || this.breakdownBusy()) return;

    this.breakdownBusy.set(true);
    this.breakdownError.set(null);
    this.breakdownProposal.set(null);
    try {
      const proposal = await this.breakdown.propose(text);
      this.breakdownProposal.set(proposal);
    } catch (e) {
      this.breakdownError.set(extractErrorMessage(e, 'Breakdown failed.'));
    } finally {
      this.breakdownBusy.set(false);
    }
  }

  protected async confirmBreakdown(): Promise<void> {
    const p = this.breakdownProposal();
    if (!p || this.breakdownConfirming()) return;

    this.breakdownConfirming.set(true);
    this.breakdownError.set(null);
    try {
      const created = await this.breakdown.materialize({
        title: p.parentTitle,
        estimateMinutes: p.subtasks.reduce((sum, s) => sum + Math.max(0, s.estimateMinutes), 0) || null,
        subtasks: p.subtasks,
      });
      // Push into the pipeline cache so the active /plan / /actions view sees
      // the new parent without a refetch. Subtasks land via realtime hub.
      this.pipeline.applyActionChange(created);
      this.discardBreakdown();
    } catch (e) {
      this.breakdownError.set(extractErrorMessage(e, 'Could not create the tasks.'));
    } finally {
      this.breakdownConfirming.set(false);
    }
  }

  protected discardBreakdown(): void {
    this.breakdownProposal.set(null);
    this.breakdownText.set('');
    this.breakdownError.set(null);
  }

  /**
   * Phase 1 SPEC v2 §6.8 Step 3 — bottom-bar timer controls. The composer
   * slot collapses into the running surface whenever a session is active.
   * Stop is a fire-and-forget against the server; realtime push clears the
   * local active signal so other tabs converge on the same state. The error
   * surface is local to the bar — if a stop fails we keep the surface
   * present (so the user can retry) and show a small toast-style line.
   */
  protected async stopTimer(): Promise<void> {
    this.timerError.set(null);
    try { await this.timer.stop(); }
    catch (e) { this.timerError.set(extractErrorMessage(e, 'Could not stop the timer.')); }
  }

  protected openRunningAction(): void {
    const a = this.timer.active();
    if (!a) return;
    void this.router.navigate(['/focus', a.actionItemId]);
  }

  protected breakdownTotalMinutes(): number {
    return this.breakdownProposal()?.subtasks.reduce(
      (sum, s) => sum + Math.max(0, s.estimateMinutes), 0,
    ) ?? 0;
  }

  /** Inline-edit support for a single subtask title from the preview card. */
  protected updateSubtaskTitle(index: number, title: string): void {
    this.breakdownProposal.update(p => {
      if (!p) return p;
      const next = p.subtasks.map((s, i) => i === index ? { ...s, title } : s);
      return { ...p, subtasks: next };
    });
  }

  /** Replies are surfaced inline above the bar; this caps how many turns we show. */
  protected readonly MAX_VISIBLE_TURNS = 6;

  protected visibleHistory(): CosTurn[] {
    const all = this.history();
    return all.length > this.MAX_VISIBLE_TURNS
      ? all.slice(all.length - this.MAX_VISIBLE_TURNS)
      : all;
  }

  protected toggleExpanded(): void {
    this.expanded.update(v => !v);
  }

  protected toggleStuckMode(): void {
    this.stuckMode.update(v => !v);
  }

  protected toggleDraftHints(): void {
    this.draftHintsExpanded.update(v => !v);
  }

  protected onDraftInput(text: string): void {
    this.draft.set(text);
    this.autoSize();
    this.scheduleDraftSearch(text);
  }

  /**
   * Debounced typing-time recall. Bounds: too-short queries are noise, too-long
   * ones are full captures (the user isn't asking, they're dumping). 500ms
   * debounce avoids per-keystroke calls. Race-guarded against the draft
   * changing while the search is in flight.
   */
  private scheduleDraftSearch(text: string): void {
    if (this.draftSearchTimer) clearTimeout(this.draftSearchTimer);
    const trimmed = text.trim();
    if (trimmed.length < 4 || trimmed.length > 200) {
      this.draftHits.set([]);
      this.draftHintsExpanded.set(false);
      return;
    }
    this.draftSearchTimer = setTimeout(async () => {
      try {
        const hits = await this.cos.searchEntries(trimmed, undefined, 5);
        if (this.draft().trim() === trimmed) {
          this.draftHits.set(hits);
        }
      } catch {
        // Silent — proactive recall is ambient, never blocks.
      }
    }, 500);
  }

  protected placeholder(): string {
    return this.stuckMode()
      ? "Whatever's on your mind. No decisions needed."
      : 'Dump anything to your Chief of Staff…';
  }

  private detectOverwhelm(text: string): boolean {
    return OVERWHELM_PATTERNS.some(p => p.test(text));
  }

  protected clearHistory(): void {
    this.history.set([]);
    this.expanded.set(false);
  }

  async send(): Promise<void> {
    const text = this.draft().trim();
    if (!text || this.busy()) return;

    // Phase 3 Pass 2 — if the message reads like distress and ease isn't already
    // on, flip it on now. The bar's visual shift is the user-facing signal; we
    // don't ask permission because asking adds the exact decision-load that
    // ease mode is meant to remove.
    if (!this.stuckMode() && this.detectOverwhelm(text)) {
      this.stuckMode.set(true);
    }

    const turn: CosTurn = { role: 'User', content: text };
    const baseHistory = this.history();
    this.history.set([...baseHistory, turn]);
    this.draft.set('');
    this.busy.set(true);
    this.error.set(null);
    this.expanded.set(true);
    this.autoSize();

    // Clear typing-time recall — the user committed to a send, the hint surface
    // shouldn't linger over the new turn.
    if (this.draftSearchTimer) clearTimeout(this.draftSearchTimer);
    this.draftHits.set([]);
    this.draftHintsExpanded.set(false);

    try {
      // Send only AgentTurn-shape items back to the API; tool metadata is
      // local-only.
      const apiHistory: AgentTurn[] = baseHistory.map(({ role, content }) => ({ role, content }));
      const resp: CosChatResponse = await this.cos.chat({
        history: apiHistory,
        message: text,
        stuckMode: this.stuckMode(),
      });
      this.history.update(h => [
        ...h,
        { role: 'Assistant', content: resp.reply, toolInvocations: resp.toolInvocations }
      ]);
    } catch (e) {
      this.error.set(extractErrorMessage(e, 'Chief of Staff is not responding.'));
    } finally {
      this.busy.set(false);
      queueMicrotask(() => this.textareaRef()?.nativeElement.focus());
    }
  }

  onKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  /** Grow the textarea up to a cap as the user types. */
  autoSize(): void {
    const el = this.textareaRef()?.nativeElement;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }

  /** "Filed: X" or "Signal captured: ..." — the receipt the COS prompt promises. */
  protected toolLabel(t: CosToolInvocation): string {
    if (t.resultPreview) return t.resultPreview;
    switch (t.name) {
      case 'capture_signal': return 'Action queued';
      case 'capture_notebook_entry': return 'Filed to notebook';
      case 'recall_notebook_entries': return 'Looked up notebook entries';
      case 'list_notebooks': return 'Listed notebooks';
      default: return t.name;
    }
  }

  /**
   * Phase 5 — chip kind for visual differentiation. Signal (action) and Notebook
   * (held thought) are categorically different destinations; the receipt thread
   * should reflect that at a glance. Recall is only ever a chip when empty
   * (otherwise it's cards via {@link isInlineRecall}).
   */
  protected toolKind(t: CosToolInvocation): 'signal' | 'notebook' | 'recall' | 'default' {
    switch (t.name) {
      case 'capture_signal': return 'signal';
      case 'capture_notebook_entry': return 'notebook';
      case 'recall_notebook_entries': return 'recall';
      default: return 'default';
    }
  }

  /**
   * Recall hits get rendered as inline cards (Phase 2 ADHD design — recall must
   * feel as good as capture). Empty recalls fall through to the chip so the user
   * still sees "Nothing on file for X" instead of silence.
   */
  protected isInlineRecall(t: CosToolInvocation): boolean {
    return t.name === 'recall_notebook_entries'
      && !!t.recallEntries
      && t.recallEntries.length > 0;
  }

  protected chipInvocations(invocations: CosToolInvocation[]): CosToolInvocation[] {
    return invocations.filter(t => !this.isInlineRecall(t));
  }

  protected recallInvocations(invocations: CosToolInvocation[]): CosToolInvocation[] {
    return invocations.filter(t => this.isInlineRecall(t));
  }

  /**
   * Attach an image — Finny screenshot intake. Replaces the standalone /capture
   * page; same UX as attaching a screenshot to a Claude conversation. The image
   * gets sent to the screenshot extractor (Claude vision) which produces a new
   * Prospect; we then navigate to that prospect's detail page so the user can
   * review and refine. Pasting an image into the textarea triggers the same path.
   */
  protected openFilePicker(): void {
    this.fileInputRef()?.nativeElement.click();
  }

  protected async onFileSelected(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    await this.handleImage(file);
  }

  protected async onPaste(event: ClipboardEvent): Promise<void> {
    const items = event.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (!file) continue;
        event.preventDefault();
        await this.handleImage(file);
        return;
      }
    }
  }

  private async handleImage(file: File): Promise<void> {
    if (this.busy()) return;
    this.busy.set(true);
    this.error.set(null);
    try {
      const created = await this.prospects.fromScreenshot(file);
      this.draft.set('');
      await this.router.navigate(['/prospects', created.id]);
    } catch (e) {
      this.error.set(extractErrorMessage(e, 'Screenshot extraction failed.'));
    } finally {
      this.busy.set(false);
    }
  }
}
